import React, { useState, useEffect, useRef } from "react";
import PageContainer from "../components/pages/PageContainer";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import DashboardHeader from "../components/Other/DashboardHeader";
import { marketSentiment, sentimentReportApi, voteMarket } from "../api/user";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import {
  AreaSeries,
  Chart,
  ChartCanvas,
  XAxis,
  YAxis,
  discontinuousTimeScaleProviderBuilder,
} from "react-financial-charts";
import { format } from "d3-format";
import moment from "moment-timezone";

// --- palette (centralised so a colour pass is a single edit) ---
const BULL_GREEN = "#4CA85F";
const BEAR_RED = "#D65A4E";
const LIVE_DOT = "#35B23C"; // brand green — "Live" (results are streaming)
const OPEN_DOT = "#0F766E"; // teal — "Voting open" (pre-vote)
// Chart: the AAII benchmark is a neutral metric, kept visually distinct from the
// green/red vote bar via a light-blue gradient area + blue stroke.
const CHART_STROKE = "#5B9BD5";
const CHART_FILL_TOP = "rgba(91, 155, 213, 0.38)";
const CHART_FILL_BOTTOM = "rgba(91, 155, 213, 0.02)";
const CHART_GRID = "#EEF2F6";
const CHART_BORDER = "#E2E8F0";
const CHART_LABEL = "#94A3B8";

// Remember which way the current user voted, keyed to the voting period so it
// clears itself when a new week opens. Backend `user_vote` is the source of
// truth once deployed; this keeps the "You voted bull/bear" line correct on
// reload (same device) even before that ships.
const VOTE_STORE_KEY = "qushiSentimentVote";
const readStoredVote = () => {
  try {
    return JSON.parse(localStorage.getItem(VOTE_STORE_KEY) || "null");
  } catch {
    return null;
  }
};
const writeStoredVote = (direction, periodEnd) => {
  try {
    localStorage.setItem(
      VOTE_STORE_KEY,
      JSON.stringify({ direction, periodEnd })
    );
  } catch {
    /* ignore quota / private-mode failures */
  }
};

// bull -> "1", bear -> "2" (matches backend VOTE_TYPE). Falls back to the
// per-device store keyed to the current voting period.
const resolveDirection = (d) => {
  if (!d) return null;
  const uv = d.user_vote;
  if (uv === "1" || uv === 1) return "bull";
  if (uv === "2" || uv === 2) return "bear";
  const stored = readStoredVote();
  if (stored && d.voting_ends_at && stored.periodEnd === d.voting_ends_at) {
    return stored.direction;
  }
  return null;
};

const MarketSentimentPage = () => {
  const [sidebarToggle] = useOutletContext();

  // Vote state
  const [data, setData] = useState(null); // { bull, bear, hasVoted, total_votes, voting_ends_at, user_vote }
  const [voting, setVoting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [voteType, setVoteType] = useState(""); // "BULL" | "BEAR"

  // AAII benchmark chart data
  const [marketSentimentData, setMarketSentimentData] = useState([]);
  const chartWrapRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  // --- data ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await sentimentReportApi();
        if (!cancelled && res?.data?.status === 200) {
          setMarketSentimentData(res?.data?.data || []);
        }
      } catch (error) {
        console.error("sentiment report failed", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await marketSentiment();
        if (!cancelled && res?.data?.data) setData(res.data.data);
      } catch (error) {
        console.error("market sentiment fetch failed", error);
        if (!cancelled) setData({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Measure the chart wrapper so the canvas fills the capped column responsively.
  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setChartWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setChartWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // --- vote actions ---
  const openModal = (type) => {
    if (hasVoted) {
      toast.error("You already voted this week. Voting resets Fridays at 4:00 PM ET.");
      return;
    }
    setVoteType(type);
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const handleVote = async () => {
    if (voting) return;
    const voteValue =
      voteType === "BULL" ? "1" : voteType === "BEAR" ? "2" : null;
    if (!voteValue) return;
    setVoting(true);
    try {
      const res = await voteMarket(voteValue);
      if (res?.status === 200) {
        const nd = res.data?.data || {};
        const direction = voteType === "BULL" ? "bull" : "bear";
        const periodEnd = nd.voting_ends_at ?? data?.voting_ends_at ?? null;
        setData((prev) => ({
          ...prev,
          bull: nd.bull ?? prev?.bull,
          bear: nd.bear ?? prev?.bear,
          hasVoted: true,
          total_votes: nd.total_votes ?? prev?.total_votes,
          voting_ends_at: periodEnd,
          user_vote: nd.user_vote ?? voteValue,
        }));
        writeStoredVote(direction, periodEnd);
        toast.success(res.data?.message || "Vote recorded");
      }
    } catch (error) {
      console.error("vote failed", error);
      toast.error("You already voted this week. Voting resets Fridays at 4:00 PM ET.");
    } finally {
      setVoting(false);
      setShowModal(false);
    }
  };

  // --- derived ---
  const hasVoted = !!data?.hasVoted;
  const bull = Number(data?.bull ?? 0);
  const bear = Number(data?.bear ?? 0);
  const totalVotes = data?.total_votes ?? data?.totalVotes ?? null;
  const voteDirection = resolveDirection(data);
  const hasRatio = bull + bear > 0;

  // State line is responsive: mobile trims to avoid wrapping; desktop keeps the
  // full "Voting resets…" reminder (there is room, and it is not shown anywhere
  // else once the vote is cast).
  const dirWord =
    voteDirection === "bull" ? "bull" : voteDirection === "bear" ? "bear" : null;
  const votedBase = dirWord
    ? `You voted ${dirWord} this week`
    : "You voted this week";
  const stateLineMobile = hasVoted ? votedBase : "Cast your weekly vote";
  const stateLineDesktop = hasVoted
    ? `${votedBase}. Voting resets Fridays at 4:00 PM ET.`
    : "Cast your weekly bull or bear vote";

  // --- chart ---
  const reportData = Array.isArray(marketSentimentData)
    ? marketSentimentData
    : [];
  const formattedData = reportData
    .map((item) => ({
      date: new Date(item.report_date),
      bull: Number(item.bull),
    }))
    .filter((d) => !isNaN(d.date.getTime()) && !isNaN(d.bull));
  const hasChart = formattedData.length > 0;
  const lastObject = reportData.length
    ? reportData[reportData.length - 1]
    : null;

  let chart = null;
  if (hasChart) {
    const ScaleProvider =
      discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date);
    const { data: cData, xScale, xAccessor, displayXAccessor } =
      ScaleProvider(formattedData);
    const bulls = formattedData.map((d) => d.bull);
    const yLo = Math.floor((Math.min(...bulls) - 2) / 5) * 5;
    const yHi = Math.ceil((Math.max(...bulls) + 2) / 5) * 5;
    chart = {
      cData,
      xScale,
      xAccessor,
      displayXAccessor,
      xExtents: [xAccessor(cData[0]), xAccessor(cData[cData.length - 1])],
      yExtents: [yLo, yHi],
    };
  }

  const chartHeight = Math.max(200, Math.min(300, Math.round(chartWidth * 0.42)));
  const areaFillStyle = (ctx, moreProps) => {
    const h = moreProps?.chartConfig?.height ?? chartHeight;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, CHART_FILL_TOP);
    grad.addColorStop(1, CHART_FILL_BOTTOM);
    return grad;
  };

  const eyebrow =
    "text-[11px] font-semibold uppercase tracking-wider text-slate-400";
  const barSegment =
    "py-4 text-sm font-bold tracking-wide text-white text-center whitespace-nowrap select-none";

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {/* No page title on this screen (desktop or mobile) — the vote bar leads. */}
      <PageHeaderTitle title="Market Sentiment" showDesktopTitle={false} />
      <PageContainer>
        <div className="px-4 md:px-8 pb-24 md:pb-12">
          <div className="mx-auto w-full max-w-4xl md:mt-4">
            {/* Mobile screen title (house rule); desktop omits it — the sidebar
                already names the active screen. */}
            <SettingsPageHeading
              title="Market Sentiment"
              className="mb-4 md:hidden"
            />

            {/* Vote bar */}
            {!hasVoted ? (
              <div className="flex w-full overflow-hidden rounded-lg">
                <button
                  type="button"
                  onClick={() => openModal("BULL")}
                  className={`${barSegment} flex-1 transition-opacity hover:opacity-95`}
                  style={{ backgroundColor: BULL_GREEN }}
                >
                  BULL
                </button>
                <button
                  type="button"
                  onClick={() => openModal("BEAR")}
                  className={`${barSegment} flex-1 transition-opacity hover:opacity-95`}
                  style={{ backgroundColor: BEAR_RED }}
                >
                  BEAR
                </button>
              </div>
            ) : (
              <div className="flex w-full overflow-hidden rounded-lg">
                <div
                  className={barSegment}
                  style={{
                    backgroundColor: BULL_GREEN,
                    flexGrow: hasRatio ? bull : 1,
                    flexBasis: 0,
                    minWidth: 88,
                  }}
                >
                  BULL {Math.round(bull)}%
                </div>
                <div
                  className={barSegment}
                  style={{
                    backgroundColor: BEAR_RED,
                    flexGrow: hasRatio ? bear : 1,
                    flexBasis: 0,
                    minWidth: 88,
                  }}
                >
                  BEAR {Math.round(bear)}%
                </div>
              </div>
            )}

            {/* State line + status pill */}
            <div className="mt-3 flex items-start justify-between gap-3">
              <p className="text-sm text-slate-500">
                <span className="md:hidden">{stateLineMobile}</span>
                <span className="hidden md:inline">{stateLineDesktop}</span>
              </p>
              <div className="flex flex-shrink-0 items-center gap-3">
                {hasVoted && totalVotes != null && (
                  <span className="whitespace-nowrap text-sm text-slate-500">
                    {Number(totalVotes).toLocaleString()}{" "}
                    {Number(totalVotes) === 1 ? "vote" : "votes"}
                  </span>
                )}
                <span className="flex items-center gap-1.5 whitespace-nowrap text-sm">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: hasVoted ? LIVE_DOT : OPEN_DOT }}
                  />
                  <span className="text-slate-600">
                    {hasVoted ? "Live" : "Voting open"}
                  </span>
                </span>
              </div>
            </div>

            {/* Content card (desktop) / bare (mobile) */}
            <div className="mt-4 w-full bg-white md:rounded-2xl md:border md:border-gray-200 md:p-8">
              <p className={eyebrow}>AAII Benchmark</p>
              <h2 className="mt-1 text-[15px] font-semibold text-slate-800">
                US Investor Sentiment, % Bullish
              </h2>

              <div ref={chartWrapRef} className="mt-4 w-full">
                {chartWidth > 0 && hasChart ? (
                  <ChartCanvas
                    height={chartHeight}
                    ratio={3}
                    width={chartWidth}
                    margin={{ left: 0, right: 26, top: 10, bottom: 24 }}
                    data={chart.cData}
                    displayXAccessor={chart.displayXAccessor}
                    seriesName="AAII"
                    xScale={chart.xScale}
                    xAccessor={chart.xAccessor}
                    xExtents={chart.xExtents}
                    disableZoom
                    disablePan
                    disableInteraction
                  >
                    <Chart id={1} yExtents={chart.yExtents}>
                      <XAxis
                        axisAt="bottom"
                        orient="bottom"
                        showTicks={false}
                        showGridLines
                        gridLinesStrokeStyle={CHART_GRID}
                        strokeStyle={CHART_BORDER}
                        tickLabelFill={CHART_LABEL}
                        fontSize={11}
                        ticks={chartWidth < 500 ? 6 : 10}
                      />
                      {/* Y axis on the right — numbers sit in a slim gutter beside
                          the plot (not over it). left margin 0 + this gutter means
                          plot + numbers together span the full width, so the chart's
                          left edge is flush with the vote bar and the numbers align
                          with the bar's right edge. */}
                      <YAxis
                        axisAt="right"
                        orient="right"
                        showTicks={false}
                        showGridLines
                        gridLinesStrokeStyle={CHART_GRID}
                        strokeStyle={CHART_BORDER}
                        tickLabelFill={CHART_LABEL}
                        fontSize={11}
                        tickFormat={format(".0f")}
                        tickPadding={4}
                        ticks={8}
                      />
                      {/* Faint enclosing box (top + left borders) */}
                      <XAxis
                        axisAt="top"
                        orient="top"
                        showTicks={false}
                        showTickLabel={false}
                        showGridLines={false}
                        strokeStyle={CHART_BORDER}
                      />
                      <YAxis
                        axisAt="left"
                        orient="left"
                        showTicks={false}
                        showTickLabel={false}
                        showGridLines={false}
                        strokeStyle={CHART_BORDER}
                      />
                      <AreaSeries
                        yAccessor={(d) => d.bull}
                        fillStyle={areaFillStyle}
                        strokeStyle={CHART_STROKE}
                        strokeWidth={2}
                      />
                    </Chart>
                  </ChartCanvas>
                ) : (
                  <div style={{ height: chartHeight }} />
                )}
              </div>

              {/* How it works */}
              <div className="mt-8 max-w-body-copy">
                <p className={eyebrow}>How it works</p>
                <div className="mt-3 space-y-4 text-sm leading-relaxed text-slate-600">
                  <p>
                    QUSHi Market Sentiment is a weekly bull or bear vote by QUSHi
                    traders. It shows how the community feels about the overall
                    market.
                  </p>
                  <p>
                    The AAII benchmark tracks bullish sentiment among individual
                    investors. It is separate from QUSHi voting. Some investors
                    read sentiment as contrarian context when readings reach
                    historical extremes.
                  </p>
                  <p>
                    Sentiment is context only, not a trading signal or
                    recommendation.
                  </p>
                </div>

                {/* Stats — two aligned columns */}
                <p className={`${eyebrow} mt-8`}>Stats</p>
                <div className="mt-3 max-w-[320px] space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Last value</span>
                    <span className="text-sm font-medium text-slate-800">
                      {lastObject ? `${lastObject.bull}%` : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Last updated</span>
                    <span className="text-sm font-medium text-slate-800">
                      {lastObject
                        ? moment(lastObject.report_date).format("MMM DD, YYYY")
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirm modal */}
        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={closeModal}
          >
            <div
              className="w-full max-w-[340px] rounded-2xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-slate-900">
                {voteType === "BULL" ? "Confirm bull call" : "Confirm bear call"}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                This locks in your weekly sentiment vote. Voting resets Fridays
                at 4:00 PM ET.
              </p>
              <button
                type="button"
                onClick={handleVote}
                disabled={voting}
                className="mt-5 w-full rounded-lg py-3 text-sm font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
                style={{
                  backgroundColor: voteType === "BULL" ? BULL_GREEN : BEAR_RED,
                }}
              >
                {voteType === "BULL" ? "Lock in bull" : "Lock in bear"}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="mt-3 w-full py-1 text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default MarketSentimentPage;
